import React, { useEffect, useRef, useState } from 'react'
import { SubFormRendererProps } from '../types/formRenderer.types'
import {
  ENTITY_TYPE,
  FieldDataByFieldNameType,
  getLayoutByEntityIdAndTypeApi,
  getLayoutByIdApi,
  getSubResources,
  IEntityFieldType,
  SEND_MANUAL_COMMUNICATION_CONSTANT,
} from '@app-store'
import { ListingTableProps } from '../types/listingTable.types'
import {
  CustomButton,
  NAMESPACE,
  Skeleton360Loader,
  SvgTrashIcon,
} from '@ui-kit'
import FormRenderer from './FormRenderer'
import { Column, EntityLayout } from '../types/layout.types'
import { noop } from 'lodash'
import { v4 } from 'uuid'
import { FieldValues, useFieldArray, useWatch } from 'react-hook-form'
import {
  getEntityData,
  getEntityDataByIdEmbedded,
} from '../apis/entityData.api'
import {
  componentNames,
  EXTRA_FIELDS,
  LAYOUT_TYPE_NAMESPACE,
  ROW_TYPES,
} from '../utils/constants'
import { TabIndexMap } from '../types/common'
import {
  generateTabIndexMapForSubForms,
  getMaxTabIndexValue,
  getMinTabIndexForPageLevel,
} from '../utils/tab-indexing'
import { NAMESPACES } from '../utils/constants'
import { RECORD_STATUS, ShortcutKeys } from '../constants/form.constants'
import { useAppSelector } from '../utils/agent.store'
import { useParams } from 'react-router-dom'
import { GroupBehaviorProvider } from './GroupBehaviorProvider'
import { LayoutPageType } from '@app-store'

const isMultiLookupRowFromMetaData = (
  metaData: IEntityFieldType | null
): boolean => {
  if (metaData?.list_view_layout_id && metaData?.create_layout_id) {
    return true
  }
  return false
}

// import { formatLayoutWithParentKeyName } from '../utils/form.utils'
const handleNestedLayout = (column: Column, parentKeyName: string) => {
  return column.elements.map((element) => {
    if (element.parent) {
      return {
        ...element,
        parent: {
          ...element.parent,
          lookupKeyName: `${parentKeyName}.${element.parent.lookupKeyName}`,
        },
      }
    }
    return {
      ...element,
      keyName: `${parentKeyName}.${element.keyName}`,
    }
  })
}
export const formatLayoutWithParentKeyName = (
  fullLayout: EntityLayout,
  parentKeyName: string,
  rowLabel?: string
): EntityLayout => {
  return {
    ...fullLayout,
    layout_data: {
      ...fullLayout.layout_data,
      added_fields: Object.fromEntries(
        Object.entries(fullLayout.layout_data.added_fields ?? {}).reduce(
          (acc: [string, FieldDataByFieldNameType[string]][], [key, value]) => {
            const newKey = `${parentKeyName}.${key}`
            if (isMultiLookupRowFromMetaData(value)) {
              acc.push([key, value])
            }

            acc.push([
              newKey,
              {
                ...value,
                selected_fields: value?.selected_fields?.map((sf) => ({
                  ...sf,
                  field_name: `${parentKeyName}.${sf.field_name}`,
                })),
                name: value?.name
                  ? `${parentKeyName}.${value?.name}`
                  : value?.name,
              },
            ])

            return acc
          },
          []
        )
      ),
      pages: fullLayout.layout_data.pages.map((page) => {
        return {
          ...page,
          rows: page.rows.map((row, rowIdx) => {
            return {
              ...row,
              ...(rowIdx === 0 && { label: rowLabel ?? row.label }),
              ...(row.lookup_meta && {
                lookup_meta: {
                  ...row?.lookup_meta,
                  lookup_keyName:
                    row?.rowType === ROW_TYPES.SUB_FORM ||
                    row?.rowType === ROW_TYPES.COMBOBOX ||
                    row?.rowType === ROW_TYPES.NOTE ||
                    row?.rowType === ROW_TYPES.NOTES ||
                    row?.rowType === ROW_TYPES.THREAD ||
                    row?.rowType === ROW_TYPES.MULTI_LOOKUP
                      ? `${parentKeyName}.${row?.lookup_meta?.lookup_keyName}`
                      : row.lookup_meta.lookup_keyName,
                },
              }),
              columns: row.columns.map((column) => {
                return {
                  ...column,
                  elements: handleNestedLayout(column, parentKeyName),
                }
              }),
            }
          }),
        }
      }),
    },
  }
}
// TODO: move to utils
export const extractFieldsFromLayout = (fullLayout: EntityLayout) => {
  return Object.keys(fullLayout.layout_data.added_fields ?? {}).filter(
    (key) => {
      if (
        fullLayout.layout_data.added_fields?.[key]?.ui_data_type ===
        componentNames.MULTI_LOOKUP
      ) {
        return false
      }
      return true
    }
  )
}

function isInteger(str: string) {
  const num = Number(str)
  return Number.isInteger(num)
}

export const removeIndexFromSubFormKeyName = (keyNameString: string) => {
  return keyNameString
    .split('.')
    .filter((str) => !isInteger(str))
    .join('.')
}

export const getKeyNameForExtraFields = (
  subFormKeyName: string,
  type: string
): string => {
  // only for storing
  // if (type.includes('layout')) {
  const newSubFormKeyName = removeIndexFromSubFormKeyName(subFormKeyName) // deeply nested ones can have index in them.
  // }
  return EXTRA_FIELDS.EXTRA_FIELD + '.' + newSubFormKeyName + type
}

// Wrapper for FormRenderer.
// TODO: Seperate for Lookup and MultiLookup
const SubFormRenderer: React.FC<SubFormRendererProps> = ({
  depth,
  row,
  layout,
  defaultExpandedCount,
  maxExpandedCount,
  tabOffset,
  addProductIdFilterForPaysign,
  ...props
}) => {
  const [mode, setMode] = useState(props?.mode)
  // set defaultValues from layout? -> should be done in parent.
  const isNested = !!props.parentFormKeyName
  const keyName = `${row?.lookup_meta?.lookup_keyName ?? '_'}`
  // having an array sibling to an object causes issues in react hook form!?
  const subFormKeyName = isNested ? `${keyName}` : `subForm.${keyName}`
  const meta = layout?.layout_data?.added_fields?.[keyName || 0]
  const entityId = meta?.relationship?.target_entity_id ?? 0
  const entityName = meta?.relationship?.target_entity ?? ''
  // const entityNameLabel = meta?.relationship?.child_label
  const relation_identifier = meta?.relationship?.relation_identifier
  const layoutId = meta?.create_layout_id ?? meta?.list_view_layout_id
  const isDataFetchedForSingleLookup = useRef(false) // to prevent loop if keyName === subFormKeyName
  // const [isDataFetchComplete, setIsDataFetchComplete] = useState(false)
  // // to add more layouts if not added by data.

  const [childLayout, setChildLayout] = useState<EntityLayout>()
  const [layoutArray, setLayoutArray] = useState<
    Array<EntityLayout & { layoutId: string }>
  >([])
  const [error, setError] = useState<string | undefined | null>()
  const [tabIndexMap, setTabIndexMap] = useState<Record<string, TabIndexMap>>(
    {}
  )
  const workflowDefinition = useAppSelector(
    (state) => state?.workflowData?.workflowDefinition
  )
  const { caseId } = useParams()
  // const renderChilds = () => {
  //   // if current subForm is rendered it means the parent's data fetch is complete.
  //   // if we have a dataId/lookupVal id we need to call our subresource / entityId api.
  //   // once we have done that we can render our children.

  //   // if we have the parent Id we can look for lookup
  //   if (isDataFetchComplete) {
  //     return true
  //   }
  //   return false
  // }

  const fillLayoutArrayWithCount = (
    count: number,
    childLayout: EntityLayout | undefined,
    _appendEmptyData = false
  ) => {
    if (!childLayout) {
      return // dispatch error loading layout
    }

    const newLayoutArray = []
    for (let i = 0; i < count; i++) {
      const newLayoutToBeAdded = {
        ...childLayout,
        layoutId: v4(),
      }
      newLayoutArray.push(newLayoutToBeAdded)
      // _appendEmptyData && append({})
    }
    setLayoutArray(newLayoutArray)

    // setLayoutArray((layoutArray) => {
    //   const moreLayoutsToAdd = count - layoutArray.length
    //   if (moreLayoutsToAdd > 0) {
    //     const newLayoutArray = [...layoutArray]
    //     for (let i = 0; i < moreLayoutsToAdd; i++) {
    //       const newLayoutToBeAdded = {
    //         ...childLayout,
    //         layoutId: v4(),
    //       }
    //       newLayoutArray.push(newLayoutToBeAdded)
    //       // _appendEmptyData && append({})
    //     }
    //     return newLayoutArray
    //   }
    //   return layoutArray
    // })
  }

  useEffect(() => {
    setMode(props.mode)
  }, [props.mode])

  const dataId = useWatch({
    name: props.parentFormKeyName ? props.parentFormKeyName + '.id' : 'id',
  })

  useEffect(() => {
    console.log('debugg', props.parentFormKeyName, dataId)
  }, [dataId])

  const { append, replace, update } = useFieldArray({
    name: props.isMultiLookup
      ? subFormKeyName
      : '_unnecessaryKey_single_lookup_field_array_',
    // name: `subformFieldArray.${keyName}`,
    // keyName: 'layoutId',
  })

  // for multilookup
  // can we just get the ids of the assigned values? -yes we can from the advanced query!
  // so now we just have to pick those values from somewhere in the form?

  // for now just use subResource api

  // for lookup we would need to fetch the data from the api.

  useEffect(() => {
    const fetchLayout = async () => {
      if (
        row?.lookup_meta?.lookup_keyName === NAMESPACES.C_PATIENT_ADDRESS ||
        row?.lookup_meta?.lookup_keyName === NAMESPACES.C_PATIENT_PHONE ||
        row?.lookup_meta?.lookup_keyName === NAMESPACES.C_CONTACT_PHONE ||
        row?.lookup_meta?.lookup_keyName === NAMESPACES.C_CONTACT_ADDRESS ||
        row?.lookup_meta?.lookup_keyName === NAMESPACES.C_PROVIDER_CONTACT
      ) {
        try {
          const res = await getLayoutByEntityIdAndTypeApi({
            entityId:
              layout?.layout_data?.added_fields?.[
                row?.lookup_meta?.lookup_keyName ?? ''
              ]?.relationship?.target_entity_id?.toString(),
            type: LAYOUT_TYPE_NAMESPACE.VIEW_EDIT_TYPE,
            isDefault: true,
          })
          if (res?.data) {
            setChildLayout(res.data?.[0])
            props.methods.setValue(
              getKeyNameForExtraFields(subFormKeyName, '_layout'),
              res.data?.[0]
            )
          }
        } catch (err) {
          console.error(err)
        }
      } else {
        try {
          const addEditViewLayout = await getLayoutByIdApi({
            entityId: entityId?.toString(),
            layoutId: layoutId?.toString(),
          })
          // const parsedLayout = parseLayout addEditViewLayout.data
          // const fullLayoutForThisSubForm:EntityLayout = addEditViewLayout.data
          setChildLayout(addEditViewLayout.data)

          props.methods.setValue(
            getKeyNameForExtraFields(subFormKeyName, '_layout'),
            addEditViewLayout.data
          )
        } catch (err) {
          console.error(err)
          setError('Layout not Found.')
        }
      }
    }
    fetchLayout()
  }, [entityId, layoutId])

  useEffect(() => {
    if (mode === 'add') return
    if (!props.isMultiLookup) return
    if (!childLayout) return
    if (!dataId) {
      // if (isNested) setIsDataFetchComplete(true)
      return
    }
    const fetchAssignedDataCallback: ListingTableProps['dataFetchCallback'] =
      async (payload) => {
        try {
          const response = await getSubResources(
            entityName || '',
            entityId?.toString() || '',
            layoutId ?? '',
            relation_identifier ?? '',
            false,
            dataId ?? '',
            +payload.page,
            +payload.size,
            undefined,
            payload.filters,
            payload.select,
            payload.related_entities
          )

          const assignedActiveRecords = response?.data?.content?.filter(
            (record: FieldValues) =>
              record?.record_status === RECORD_STATUS.ACTIVE
          )
          props.methods.setValue(
            `__not_needed_layoutArray__`,
            assignedActiveRecords
          )
          props.methods.setValue(
            getKeyNameForExtraFields(subFormKeyName, '_initialValues'),
            response.data.content
          )

          // append empty before setting the value?
          // append(Array(assignedActiveRecords?.length,))

          // const newFieldsToAdd =
          //   response.data?.content.length - layoutArray.length
          // const arrayToAppend = []
          // for (let i = 0; i < newFieldsToAdd; i++) {
          //   arrayToAppend.push({})
          // }
          // append(arrayToAppend)
          // append(response.data?.content) // setValue because reset sets this as an array at the start!?

          // const newLayoutArray = []
          // for (let i = 0; i < assignedActiveRecords?.length; i++) {
          //   const newLayoutToBeAdded = {
          //     ...childLayout,
          //     layoutId: v4(),
          //   }
          //   newLayoutArray.push(newLayoutToBeAdded)
          //   // _appendEmptyData && append({})
          // }
          // setLayoutArray(newLayoutArray)
          if (
            defaultExpandedCount !== undefined &&
            assignedActiveRecords?.length > defaultExpandedCount
          ) {
            replace(assignedActiveRecords)
            fillLayoutArrayWithCount(
              assignedActiveRecords?.length,
              childLayout,
              false
            )
          } else {
            // props.methods.setValue(subFormKeyName, assignedActiveRecords)
            replace(assignedActiveRecords)
            // defaultExpandedCount is <= assigned active records
            fillLayoutArrayWithCount(
              defaultExpandedCount || assignedActiveRecords?.length,
              childLayout,
              false
            )
          }
          // props.methods.setValue(subFormKeyName, response.data?.content)

          // append(assignedActiveRecords)
          // fillLayoutArrayWithCount(
          //   assignedActiveRecords?.length,
          //   childLayout,
          //   false
          // )
          // setIsDataFetchComplete(true)

          return response.data
        } catch (err) {
          console.error(err)
          // setError(`Unable to fetch ${entityName} data`)
        }
      }
    const callFetchAssignedDataCallbackWithFilters = async (
      addProductIdFilterForPaysign?: boolean
    ) => {
      if (addProductIdFilterForPaysign) {
        const response = await getEntityDataByIdEmbedded(
          workflowDefinition?.global_variables?.entity_name ?? '',
          Number(workflowDefinition?.global_variables?.entity_id),
          [SEND_MANUAL_COMMUNICATION_CONSTANT.PRODUCT_ENTITY_NAME],
          Number(caseId)
        )
        if (response?.data) {
          const productId =
            response?.data?.[
              SEND_MANUAL_COMMUNICATION_CONSTANT.PRODUCT_ENTITY_NAME
            ]?.id
          const patientCopayCardFilters = [
            {
              criteria: [
                {
                  field_name: 'product_id',
                  values: [productId],
                  operation: 'EQUALS',
                },
              ],
            },
          ]
          await fetchAssignedDataCallback({
            page: 0,
            size: 1000,
            entity_id: entityId.toString(),
            select: extractFieldsFromLayout(childLayout),
            filters: patientCopayCardFilters,
            layout_id: +(layoutId ?? 0),
          })
        }
      } else {
        fetchAssignedDataCallback({
          page: 0,
          size: 1000,
          entity_id: entityId.toString(),
          select: extractFieldsFromLayout(childLayout),
          filters: [],
          layout_id: +(layoutId ?? 0),
        })
      }
    }
    callFetchAssignedDataCallbackWithFilters(addProductIdFilterForPaysign)
  }, [
    childLayout,
    dataId,
    entityId,
    entityName,
    layoutId,
    mode,
    props.isMultiLookup,
    props.methods,
    relation_identifier,
    replace,
    subFormKeyName,
    addProductIdFilterForPaysign,
  ])

  const lookupVal = useWatch({
    // control,
    name: props.isMultiLookup ? '__MultiLookupWatch__' : keyName,
    // defaultValue: "default", // default value before the render
  })
  // we watch the single lookup to get the id to fill the form with.

  const handleTrashIconClick = (
    layout: EntityLayout & {
      layoutId: string
    }
  ) => {
    const index = layoutArray.findIndex((l) => l.layoutId === layout.layoutId)
    setLayoutArray((prevLayouts) =>
      prevLayouts.map((l) =>
        l.layoutId === layout.layoutId
          ? { ...l, _state: NAMESPACES?.REMOVED }
          : l
      )
    )
    if (index !== -1) {
      update(index, {
        ...props.methods.getValues(subFormKeyName)[index],
        _state: NAMESPACES?.REMOVED,
      })
    }
  }
  useEffect(() => {
    if (props.isMultiLookup) return
    // fetch data for lookup using relationship identifier OR value set in lookup?
    if (isDataFetchedForSingleLookup.current) return
    if (!childLayout) return
    if (!lookupVal?.id) {
      // if it is not nested we don't know if parent data has arrived or not. and parent id has arrived or not.
      // so only when it's nested we can say data fetch complete
      // if (isNested) {
      //   setIsDataFetchComplete(true)
      // }
      return
    }

    const fetchEntityData = async (id: number) => {
      try {
        const entity_data = await getEntityData(
          entityName,
          entityId,
          Number(layoutId),
          id
        )
        if (entity_data.data?.id) {
          // we can also assume we will always get data if we have a lookup value.
          props.methods.setValue(subFormKeyName, entity_data.data)
          // fillLayoutArrayWithCount(1, childLayout)
          props.methods.setValue(
            getKeyNameForExtraFields(subFormKeyName, '_initialValues'),
            entity_data.data
          )
        }
        // setIsDataFetchComplete(true)
      } catch (err) {
        console.error(err)
      }
    }
    fetchEntityData(lookupVal?.id)
    isDataFetchedForSingleLookup.current = true
  }, [
    childLayout,
    entityId,
    entityName,
    keyName,
    layoutArray,
    layoutId,
    lookupVal,
    props.isMultiLookup,
    props.methods,
    subFormKeyName,
  ])

  useEffect(() => {
    if (!defaultExpandedCount) return
    if (!childLayout) return
    // if (props.isMultiLookup && isDataFetchComplete) {
    // because we use append with multilookup instead of setValue so we fill after we have data.
    if (props.isMultiLookup) {
      const newFieldsToAdd = defaultExpandedCount - layoutArray.length
      const arrayToAppend = []
      for (let i = 0; i < newFieldsToAdd; i++) {
        arrayToAppend.push({
          // ...
          // ...Object.fromEntries(
          //   Object.entries(childLayout.layout_data.added_fields ?? {}).map(
          //     ([keyName, _value]) => [keyName, '']
          //   )
          // ),
        })
      }
      append(arrayToAppend, { shouldFocus: false })
      fillLayoutArrayWithCount(defaultExpandedCount, childLayout, true)
    } else {
      fillLayoutArrayWithCount(defaultExpandedCount, childLayout, false)
    }
  }, [defaultExpandedCount, childLayout, props.isMultiLookup])
  useEffect(() => {
    if (layoutArray?.length) {
      setTabIndexMap(generateTabIndexMapForSubForms(layoutArray, tabOffset))
    } else {
      setTabIndexMap({})
    }
  }, [layoutArray, tabOffset])
  if (defaultExpandedCount && defaultExpandedCount > 0 && !childLayout) {
    return <Skeleton360Loader />
  }

  if (error) {
    return <div>{error}</div>
  }

  if (defaultExpandedCount && defaultExpandedCount > 0 && !childLayout) {
    return <Skeleton360Loader />
  }

  if (error) {
    return <div>{error}</div>
  }

  if (childLayout?.entity?.entity_type === ENTITY_TYPE?.VIEW) {
    return null
  }

  return (
    <div>
      {layoutArray.map((layout, idx) => {
        const formattedKeyName = props.isMultiLookup
          ? `${subFormKeyName}.${idx}`
          : `${subFormKeyName}`
        const formattedLayout = formatLayoutWithParentKeyName(
          layout,
          formattedKeyName,
          row?.label?.split('|')?.[idx] ?? row?.label?.split('|')?.[0]
        ) // TODO: refactor this to improve performance by doing it inside RowRender by prop drilling a parentKeyName prop to FormRenderer
        return layout?._state === NAMESPACES?.REMOVED ? null : (
          <div key={layout?.layoutId} className="flex flex-col">
            <div className="flex justify-end w-full px-2">
              <div className="text-sm font-medium hidden">{`${formattedKeyName} ${entityId} ${layoutId} 
              ${
                props.isMultiLookup
                  ? componentNames.MULTI_LOOKUP
                  : componentNames.SINGLE_LOOKUP
              }`}</div>
              <div
                className={`${
                  defaultExpandedCount !== undefined &&
                  maxExpandedCount !== undefined &&
                  idx >= defaultExpandedCount &&
                  mode !== 'view'
                    ? ''
                    : 'hidden'
                }`}
              >
                <SvgTrashIcon
                  className="cursor-pointer"
                  onClick={() => handleTrashIconClick(layout)}
                  tabIndex={
                    tabIndexMap?.[layout?.layoutId]
                      ? getMinTabIndexForPageLevel(
                          tabIndexMap?.[layout?.layoutId]
                        )
                      : 0
                  }
                  onKeyDown={(e) => {
                    if (
                      e?.key === ShortcutKeys.Enter ||
                      e?.key === ShortcutKeys.Space
                    ) {
                      e?.preventDefault()
                      handleTrashIconClick(layout)
                    }
                  }}
                  width={20}
                  height={20}
                />
              </div>
            </div>
            <GroupBehaviorProvider
              groups={childLayout?.layout_data?.groups}
              pages={
                (childLayout?.layout_data?.pages ??
                  []) as unknown as LayoutPageType[]
              }
              entityName={'macros'}
              recordId={caseId}
            >
              <FormRenderer
                parentFormKeyName={formattedKeyName}
                // key={layout.layoutId}
                reregisterKey={layout.layoutId}
                heading={undefined}
                layout={formattedLayout.layout_data}
                mode={mode}
                handleFormSubmit={noop}
                fullLayout={formattedLayout}
                methods={props.methods}
                formUniqueId={props.formUniqueId}
                depth={depth === undefined ? depth : depth + 1}
                tabIndexMap={tabIndexMap?.[layout?.layoutId]}
                subFormMetaObject={{
                  subFormIndex: idx,
                  isSubFormMulti: !!props.isMultiLookup,
                }}
                layoutRowType={props.layoutRowType}
              />
            </GroupBehaviorProvider>
          </div>
        )
      })}
      {mode !== 'view' &&
        ((!props?.isMultiLookup && layoutArray.length < 1) ||
          (props?.isMultiLookup &&
            maxExpandedCount !== undefined &&
            layoutArray?.filter(
              (item: FieldValues) => item?._state !== NAMESPACE.REMOVED
            )?.length < maxExpandedCount)) && (
          <div className="flex justify-end w-full">
            <CustomButton
              variant="primary"
              type="button"
              widthClass="m-w-20"
              onClick={() => {
                console.log(layout)
                if (!childLayout) {
                  return // dispatch error loading layout?
                }
                const newLayoutToBeAdded = {
                  ...childLayout,
                  layoutId: v4(),
                }
                setLayoutArray([...layoutArray, newLayoutToBeAdded])
                props.isMultiLookup && append({}, { shouldFocus: false })
              }}
              tabIndex={
                getMaxTabIndexValue(tabIndexMap)
                  ? getMaxTabIndexValue(tabIndexMap) + 1
                  : 0
              }
            >
              + Add
              {/* {entityNameLabel} */}
            </CustomButton>
          </div>
        )}
    </div>
  )
}

export default SubFormRenderer
